import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Save, Clock, Loader2, CheckCircle, XCircle,
  CalendarDays, Plus, Edit, Trash2, Mail, Users, Bell, AlertTriangle, Eye, Search,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import SiteAnnouncementBanner from "@/components/SiteAnnouncementBanner";
import { EventFormModal } from "@/components/EventFormModal";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { MarketProfileForm } from "@/components/forms/MarketProfileForm";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { EmailLogDialog } from "@/components/EmailLogDialog";
import { EmailBetaOverlay } from "@/components/EmailBetaOverlay";
import { AnnouncementsPanel } from "@/components/AnnouncementsPanel";
import { BusinessDetailModal } from "@/pages/DirectoryPage";
import { normalizeUrlFields } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toSearchableList = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const usableIdentifier = (value) => {
  const text = String(value || "").trim();
  return text && !["undefined", "null", "none"].includes(text.toLowerCase()) ? text : "";
};

const vendorIdentifier = (business) => (
  usableIdentifier(business?.id) ||
  usableIdentifier(business?._id) ||
  usableIdentifier(business?.business_email)
);

const addVendorStatuses = (businesses, vendorRequests) => {
  const statusByBusinessId = new Map(
    (vendorRequests || [])
      .filter((request) => request.business_id || request.id)
      .map((request) => [request.business_id || request.id, request.status])
  );

  return (businesses || []).map((business) => {
    const businessId = vendorIdentifier(business);
    return {
      ...business,
      id: businessId,
      vendor_status: business.vendor_status || statusByBusinessId.get(businessId) || "",
    };
  });
};

const formatEmailLogDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getLogoUrl = (logo) => {
  if (!logo) return "";
  if (logo.startsWith("http")) return logo;
  return `${BACKEND_URL}${logo}`;
};

const normalizeMarket = (market) => ({
  ...market,
  season_year_round: !!market?.season_year_round,
  hours_event_based: !!market?.hours_event_based,
});

const formatEventTimeRange = (event) => {
  if (event.time && event.end_time) return `${event.time} - ${event.end_time}`;
  return event.time || "";
};

const US_STATE_PATTERN = "(?:A[LKSZR]|C[AOT]|D[CE]|F[LM]|G[AU]|HI|I[ADLN]|K[SY]|LA|M[ADEHINOST]|N[CDEHJMVY]|O[HKR]|P[AWR]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY]|DC|Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming|District of Columbia)";

const isValidAddress = (value) => {
  const text = String(value || "").trim().replace(/\s+/g, " ");
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  const streetPart = parts[0] || text;
  const hasStreet = /\b\d{1,6}\b/.test(streetPart) && /[a-z]/i.test(streetPart);
  const hasState = new RegExp(`\\b${US_STATE_PATTERN}\\b`, "i").test(text);
  const hasZip = /\b\d{5}(?:-\d{4})?\b/.test(text);
  const hasCity = parts.length >= 3 || new RegExp(`[A-Za-z][A-Za-z .'-]{2,}\\s+${US_STATE_PATTERN}\\s+\\d{5}`, "i").test(text);
  return hasStreet && hasCity && hasState && hasZip;
};

const MarketDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("listing");
  const [markets, setMarkets] = useState([]);
  const [selectedMarketId, setSelectedMarketId] = useState("");
  const [addingMarket, setAddingMarket] = useState(false);
  const [market, setMarket] = useState({
    market_name: "", email: "", instagram: "", facebook: "", tiktok: "", website: "",
    season_start: "", season_end: "", season_year_round: false,
    hours: {}, hours_event_based: false, address: "", location: "", logo: "", description: "",
  });
  const [myEvents, setMyEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [emailList, setEmailList] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailDraft, setEmailDraft] = useState(null);
  const [vendorRequests, setVendorRequests] = useState([]);
  const [vendorRequestStatus, setVendorRequestStatus] = useState("pending");
  const [selectedVendorBusiness, setSelectedVendorBusiness] = useState(null);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [vendorSearchTerm, setVendorSearchTerm] = useState("");
  const [loadingAvailableVendors, setLoadingAvailableVendors] = useState(false);
  const [addingVendorId, setAddingVendorId] = useState("");
  const [updatingVendorRequest, setUpdatingVendorRequest] = useState("");
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [selectedEmailLog, setSelectedEmailLog] = useState(null);
  const [addressError, setAddressError] = useState(false);

  const selectedMarketQuery = selectedMarketId ? `?market_id=${encodeURIComponent(selectedMarketId)}` : "";
  const selectedEventQuery = selectedMarketId ? `?host_id=${encodeURIComponent(selectedMarketId)}` : "";
  const vendorRequestCounts = useMemo(() => ({
    pending: vendorRequests.filter((request) => request.status === "pending").length,
    approved: vendorRequests.filter((request) => request.status === "approved").length,
    rejected: vendorRequests.filter((request) => request.status === "rejected").length,
  }), [vendorRequests]);
  const filteredVendorRequests = useMemo(
    () => vendorRequests.filter((request) => request.status === vendorRequestStatus),
    [vendorRequests, vendorRequestStatus]
  );
  const filteredAvailableVendors = useMemo(() => {
    const search = vendorSearchTerm.trim().toLowerCase();
    if (!search) return availableVendors;
    return availableVendors.filter((business) => (
      business.business_name?.toLowerCase().includes(search) ||
      business.business_email?.toLowerCase().includes(search) ||
      business.business_type?.toLowerCase().includes(search) ||
      business.business_description?.toLowerCase().includes(search) ||
      toSearchableList(business.business_locations).some((location) => String(location).toLowerCase().includes(search)) ||
      toSearchableList(business.business_categories).some((category) => String(category).toLowerCase().includes(search))
    ));
  }, [availableVendors, vendorSearchTerm]);

  const fetchMarket = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/markets/mine`, { headers: { Authorization: `Bearer ${token}` } });
      const listings = (r.data || []).map(normalizeMarket);
      setMarkets(listings);
      if (listings.length) {
        const nextSelectedId = selectedMarketId && listings.some((listing) => listing.id === selectedMarketId)
          ? selectedMarketId
          : listings[0].id;
        setSelectedMarketId(nextSelectedId);
        setMarket(listings.find((listing) => listing.id === nextSelectedId) || listings[0]);
      } else {
        const fallback = await axios.get(`${API}/market`, { headers: { Authorization: `Bearer ${token}` } });
        setMarket(normalizeMarket(fallback.data));
      }
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally { setLoading(false); }
  }, [token, logout, selectedMarketId]);

  const fetchMyEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events/my${selectedEventQuery}`, { headers: { Authorization: `Bearer ${token}` } });
      setMyEvents(r.data);
    } catch { /* ignore */ }
  }, [token, selectedEventQuery]);

  const fetchEmailList = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [listResponse, logResponse, draftResponse] = await Promise.all([
        axios.get(`${API}/market/email-list${selectedMarketQuery}`, { headers }),
        axios.get(`${API}/market/email-list/sends${selectedMarketQuery}`, { headers }),
        axios.get(`${API}/market/email-list/draft${selectedMarketQuery}`, { headers }),
      ]);
      setEmailList(listResponse.data);
      setEmailLogs(logResponse.data || []);
      const draft = draftResponse.data || null;
      setEmailDraft(draft);
      setEmailSubject(draft?.subject || "");
      setEmailMessage(draft?.message || "");
    } catch { /* ignore until market profile is available */ }
  }, [token, selectedMarketQuery]);

  const fetchVendorRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/vendor-requests${selectedMarketQuery}`, { headers: { Authorization: `Bearer ${token}` } });
      setVendorRequests(response.data || []);
    } catch {
      setVendorRequests([]);
    }
  }, [token, selectedMarketQuery]);

  const fetchAvailableVendors = useCallback(async () => {
    setLoadingAvailableVendors(true);
    try {
      const [marketResponse, directoryResponse] = await Promise.allSettled([
        axios.get(`${API}/market/available-vendors${selectedMarketQuery}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/directory`),
      ]);

      const marketVendors = marketResponse.status === "fulfilled" ? marketResponse.value.data || [] : [];
      const directoryBusinesses = directoryResponse.status === "fulfilled" ? directoryResponse.value.data || [] : [];
      const businesses = marketVendors.length ? marketVendors : directoryBusinesses;

      if (!businesses.length && marketResponse.status === "rejected" && directoryResponse.status === "rejected") {
        throw marketResponse.reason;
      }

      setAvailableVendors(addVendorStatuses(businesses, vendorRequests));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load businesses");
      setAvailableVendors([]);
    } finally {
      setLoadingAvailableVendors(false);
    }
  }, [token, selectedMarketQuery, vendorRequests]);

  const fetchAnnouncementCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/announcements`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncementUnreadCount(response.data.unread_count || 0);
    } catch { /* announcements are optional on dashboard load */ }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchMarket();
    fetchAnnouncementCount();
  }, [token, navigate, fetchMarket, fetchAnnouncementCount]);

  useEffect(() => {
    if (!token || loading) return;
    fetchMyEvents();
    fetchEmailList();
    fetchVendorRequests();
  }, [token, loading, selectedMarketId, fetchMyEvents, fetchEmailList, fetchVendorRequests]);

  const handleMarketSwitch = (marketId) => {
    const nextMarket = markets.find((listing) => listing.id === marketId);
    if (!nextMarket) return;
    setSelectedMarketId(marketId);
    setMarket(nextMarket);
    setHasChanges(false);
    setEmailList(null);
    setEmailLogs([]);
    setEmailDraft(null);
    setEmailSubject("");
    setEmailMessage("");
    setVendorRequests([]);
    setSelectedVendorBusiness(null);
    setShowAddVendorModal(false);
    setAvailableVendors([]);
    setVendorSearchTerm("");
    setVendorRequestStatus("pending");
  };

  const handleAddMarketListing = async () => {
    setAddingMarket(true);
    try {
      const response = await axios.post(`${API}/markets/mine`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const nextMarket = normalizeMarket(response.data);
      setMarkets((previous) => [nextMarket, ...previous]);
      setSelectedMarketId(nextMarket.id);
      setMarket(nextMarket);
      setDashboardTab("listing");
      setHasChanges(true);
      toast.success("New market listing added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add market listing");
    } finally {
      setAddingMarket(false);
    }
  };

  const handleChange = (field, value) => {
    setMarket((p) => ({ ...p, [field]: value }));
    setAddressError(false);
    setHasChanges(true);
  };

  const handleSeasonYearRoundChange = async (value) => {
    setMarket((p) => ({ ...p, season_year_round: value }));
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/market${selectedMarketQuery}`,
        { season_year_round: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const savedMarket = normalizeMarket(response.data);
      setMarket(savedMarket);
      setMarkets((previous) => previous.map((listing) => listing.id === savedMarket.id ? savedMarket : listing));
      toast.success("Changes saved");
    } catch (e) {
      setMarket((p) => ({ ...p, season_year_round: !value }));
      toast.error(e.response?.data?.detail || "Failed to update year-round market setting");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!String(market.location || "").trim()) {
      toast.error("Select a city or region");
      return;
    }
    if (!isValidAddress(market.address)) {
      setAddressError(true);
      toast.error("Enter a full market address with street, city, state, and ZIP");
      return;
    }
    setAddressError(false);
    setSaving(true);
    try {
      const updateData = normalizeUrlFields({
        market_name: market.market_name, website: market.website, address: market.address,
        location: market.location,
        season_start: market.season_start, season_end: market.season_end,
        season_year_round: !!market.season_year_round, hours: market.hours,
        hours_event_based: !!market.hours_event_based,
        instagram: market.instagram, facebook: market.facebook, tiktok: market.tiktok, logo: market.logo,
        description: market.description,
      });
      const r = await axios.put(`${API}/market${selectedMarketQuery}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
      const savedMarket = normalizeMarket(r.data);
      setMarket(savedMarket);
      setMarkets((previous) => previous.map((listing) => listing.id === savedMarket.id ? savedMarket : listing));
      toast.success("Changes saved");
      setHasChanges(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save changes");
    } finally { setSaving(false); }
  };

  const handleDeleteEvent = async (eventId) => {
    setDeletingEventId(eventId);
    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Event deleted");
      fetchMyEvents();
    } catch { toast.error("Failed to delete event"); }
    finally { setDeletingEventId(null); }
  };

  const handleSendMessage = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/market/email-list/send-message${selectedMarketQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Email sent successfully");
      setEmailSubject("");
      setEmailMessage("");
      setEmailDraft(null);
      fetchEmailList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingTestEmail(true);
    try {
      await axios.post(`${API}/market/email-list/send-test${selectedMarketQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Test email sent to your inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSaveEmailDraft = async () => {
    if (!emailSubject.trim() && !emailMessage.trim()) {
      toast.error("Add a subject or message before saving a draft");
      return;
    }
    setSavingDraft(true);
    try {
      const response = await axios.put(`${API}/market/email-list/draft${selectedMarketQuery}`, {
        subject: emailSubject,
        message: emailMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEmailDraft(response.data);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDeleteEmailDraft = async () => {
    setSavingDraft(true);
    try {
      await axios.delete(`${API}/market/email-list/draft${selectedMarketQuery}`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailDraft(null);
      toast.success("Draft deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleVendorRequestAction = async (businessId, status) => {
    setUpdatingVendorRequest(`${businessId}-${status}`);
    try {
      await axios.post(
        `${API}/market/vendor-requests/${businessId}${selectedMarketQuery}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === "approved" ? "Vendor approved" : "Vendor not approved");
      await fetchVendorRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update vendor request");
    } finally {
      setUpdatingVendorRequest("");
    }
  };

  const openAddVendorModal = () => {
    setVendorSearchTerm("");
    setShowAddVendorModal(true);
    fetchAvailableVendors();
  };

  const handleAddVendor = async (business) => {
    const businessId = vendorIdentifier(business);
    setAddingVendorId(businessId);
    try {
      const requestConfig = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { business_id: businessId, business };
      try {
        await axios.post(`${API}/market/vendors${selectedMarketQuery}`, payload, requestConfig);
      } catch (error) {
        if (error.response?.status !== 404) throw error;
        await axios.post(`${API}/market/vendors/${encodeURIComponent(businessId)}${selectedMarketQuery}`, { business }, requestConfig);
      }
      toast.success("Vendor added to market");
      setAvailableVendors((current) => current.map((business) => (
        business.id === businessId ? { ...business, vendor_status: "approved" } : business
      )));
      await fetchVendorRequests();
      setVendorRequestStatus("approved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add vendor");
    } finally {
      setAddingVendorId("");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  const emailAccessEnabled = !!(market?.subscription_active || market?.admin_subscription_override || emailList?.email_access_enabled);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SiteAnnouncementBanner />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {market?.approval_status === "pending" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="market-pending-banner">
            <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Your market listing is pending admin approval. This note will disappear once your listing is approved.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Market Listing</h1>
            <p className="text-slate-500 mt-1">Manage your market information</p>
          </div>
          {dashboardTab === "listing" && (
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="h-10 px-6 rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold disabled:opacity-50" data-testid="market-save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                : hasChanges ? <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                : <><CheckCircle className="w-4 h-4 mr-2" /> Saved</>}
            </Button>
          )}
        </div>

        {dashboardTab === "listing" && hasChanges && (
          <div className="fixed bottom-5 left-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-3xl -translate-x-1/2 flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50/95 p-4 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:justify-between" data-testid="market-unsaved-changes-notice">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
              <p className="text-sm font-medium text-amber-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                You have unsaved profile changes.
              </p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="h-10 rounded-full bg-[#1a1a1a] px-5 font-bold text-white hover:bg-black disabled:opacity-50" data-testid="floating-market-save-btn" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
            </Button>
          </div>
        )}

        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.12em] text-slate-400">Active market</p>
            <p className="mt-1 text-sm text-slate-500">Switch between the market listings connected to this email account.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={selectedMarketId} onValueChange={handleMarketSwitch}>
              {markets.length === 0 ? (
                <SelectTrigger className="h-11 min-w-[16rem] rounded-full border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm" data-testid="market-listing-switcher">
                  <SelectValue placeholder="Current market" />
                </SelectTrigger>
              ) : (
                <>
                  <SelectTrigger className="h-11 min-w-[16rem] rounded-full border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm" data-testid="market-listing-switcher">
                    <SelectValue placeholder="Current market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map((listing) => (
                      <SelectItem key={listing.id} value={listing.id}>
                        {listing.market_name || "Untitled Market"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </>
              )}
            </Select>
            <Button
              type="button"
              onClick={handleAddMarketListing}
              disabled={addingMarket}
              variant="outline"
              className="h-11 rounded-full bg-white px-5 font-bold"
              data-testid="add-market-listing-btn"
            >
              {addingMarket ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Add Market
            </Button>
          </div>
        </div>

        <Tabs value={dashboardTab} onValueChange={setDashboardTab} className="space-y-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <TabsList className="h-auto flex-wrap justify-start rounded-full bg-gray-100 p-1">
              <TabsTrigger value="listing" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Manage My Listing</TabsTrigger>
              <TabsTrigger value="events" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Events</TabsTrigger>
              <TabsTrigger value="vendors" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">
                Vendors
                {vendorRequestCounts.pending > 0 && (
                  <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs text-[#1a1a1a]">{vendorRequestCounts.pending}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="email" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">Email List/Messages</TabsTrigger>
            </TabsList>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDashboardTab("announcements")}
              className={`h-11 w-fit rounded-full px-5 font-bold shadow-sm ${dashboardTab === "announcements" ? "border-[#1a1a1a] bg-[#1a1a1a] text-white hover:bg-[#1a1a1a] hover:text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="market-announcements-tab-button"
            >
              <Bell className="mr-2 h-4 w-4" /> Announcements
              {announcementUnreadCount > 0 && (
                <span className="ml-2 rounded-full bg-[#42D78A] px-2 py-0.5 text-xs font-black text-[#1a1a1a]">{announcementUnreadCount}</span>
              )}
            </Button>
          </div>

          <TabsContent value="listing" className="mt-0">
            <div className="space-y-6">
              <MarketProfileForm
                data={market}
                onChange={handleChange}
                onSeasonYearRoundChange={handleSeasonYearRoundChange}
                token={token}
                uploadEndpoint={`/api/market/upload-logo${selectedMarketQuery}`}
                addressError={addressError}
                prefixSlot={<SubscriptionCard token={token} dashboardType="market" />}
              />
            </div>
          </TabsContent>

          <TabsContent value="vendors" className="mt-0">
            <Card className="bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-vendor-requests-card">
              <CardHeader>
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-[#42D78A]" /> Vendor Verification Requests
                  </CardTitle>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button
                      type="button"
                      onClick={openAddVendorModal}
                      className="rounded-full bg-[#42D78A] px-5 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]"
                      data-testid="open-add-vendor-modal"
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Vendor
                    </Button>
                    <div className="flex w-fit flex-wrap rounded-full bg-gray-100 p-1">
                      {[
                        ["pending", "Pending", vendorRequestCounts.pending],
                        ["approved", "Approved", vendorRequestCounts.approved],
                        ["rejected", "Not Approved", vendorRequestCounts.rejected],
                      ].map(([status, label, count]) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setVendorRequestStatus(status)}
                          className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${vendorRequestStatus === status ? "bg-[#1a1a1a] text-white" : "text-slate-600 hover:text-[#1a1a1a]"}`}
                          data-testid={`vendor-requests-${status}-tab`}
                        >
                          {label}
                          <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${vendorRequestStatus === status ? "bg-white text-[#1a1a1a]" : "bg-white text-slate-500"}`}>{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {filteredVendorRequests.length === 0 ? (
                  <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    No {vendorRequestStatus === "rejected" ? "not approved" : vendorRequestStatus} vendor requests for this market yet.
                  </p>
                ) : filteredVendorRequests.map((request) => (
                  <div key={`${request.business_id}-${request.requested_at}`} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <button
                        type="button"
                        onClick={() => setSelectedVendorBusiness(request)}
                        className="min-w-0 flex-1 text-left"
                        data-testid={`view-vendor-business-card-${request.business_id}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{request.business_name}</p>
                          <Badge className={request.status === "approved" ? "bg-emerald-100 text-emerald-800" : request.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>
                            {request.status === "approved" ? "Approved" : request.status === "rejected" ? "Not Approved" : "Pending"}
                          </Badge>
                        </div>
                        <p className="mt-1 text-sm text-slate-500">{request.business_email}</p>
                        {(request.business_type || request.business_categories?.length > 0) && (
                          <p className="mt-2 text-sm text-slate-600">
                            {[request.business_type, ...(request.business_categories || [])].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        {request.business_description && (
                          <p className="mt-2 line-clamp-2 text-sm text-slate-500">{request.business_description}</p>
                        )}
                      </button>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedVendorBusiness(request)}
                          className="rounded-full bg-white text-slate-700 hover:bg-slate-100"
                          data-testid={`view-vendor-business-${request.business_id}`}
                        >
                          <Eye className="mr-1 h-4 w-4" /> View Profile
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleVendorRequestAction(request.business_id, "approved")}
                          disabled={updatingVendorRequest.startsWith(request.business_id)}
                          className="rounded-full bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c]"
                          data-testid={`approve-vendor-${request.business_id}`}
                        >
                          {updatingVendorRequest === `${request.business_id}-approved` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-1 h-4 w-4" />} Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleVendorRequestAction(request.business_id, "rejected")}
                          disabled={updatingVendorRequest.startsWith(request.business_id)}
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`reject-vendor-${request.business_id}`}
                        >
                          {updatingVendorRequest === `${request.business_id}-rejected` ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <XCircle className="mr-1 h-4 w-4" />} Not Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="events" className="mt-0">
            <Card className="col-span-1 md:col-span-12 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-my-events-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="w-5 h-5 text-[#42D78A]" /> My Events
                </CardTitle>
                <Button onClick={() => { setEditingEvent(null); setShowEventForm(true); }} className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-5 py-2 text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="market-submit-event-btn">
                  <Plus className="w-4 h-4 mr-1" /> Submit Event
                </Button>
              </CardHeader>
              <CardContent>
                {myEvents.length === 0 ? (
                  <p className="text-gray-500 text-sm py-4">No events submitted yet. Submit one for the community!</p>
                ) : (
                  <div className="space-y-3">
                    {myEvents.map((event) => (
                      <div key={event.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl">
                        <div className="min-w-0 flex-1 mr-4">
                          <p className="font-medium text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{event.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {event.date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{event.date}</span>}
                            {formatEventTimeRange(event) && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatEventTimeRange(event)}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={event.approval_status === "pending" ? "bg-yellow-100 text-yellow-800" : event.approval_status === "rejected" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>{event.approval_status}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingEvent(event); setShowEventForm(true); }} className="text-slate-600 hover:text-slate-900 h-8 w-8 p-0" data-testid={`edit-event-${event.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.id)} disabled={deletingEventId === event.id} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" data-testid={`delete-event-${event.id}`}>
                            {deletingEventId === event.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email" className="mt-0">
            <div className="relative space-y-6 overflow-hidden rounded-2xl">
              {!emailAccessEnabled && <EmailBetaOverlay testId="market-email-beta-overlay" />}
              <Card className="bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="market-email-list-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-[#42D78A]" /> Email List/Messages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <p className="text-sm font-medium">This feature is in BETA and there may be bugs.</p>
                </div>
                <div className="flex flex-col gap-3 rounded-xl bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{emailList?.name || "Your shopper list"}</p>
                    <p className="text-sm text-slate-500">Follower emails stay private. You can only see the count.</p>
                  </div>
                  <Badge className="w-fit bg-[#42D78A]/15 px-3 py-1 text-[#157347]">
                    <Users className="mr-1.5 h-3.5 w-3.5" /> {emailList?.active_member_count || 0} followers
                  </Badge>
                </div>
                <div className={!emailAccessEnabled ? "pointer-events-none select-none opacity-30" : ""} aria-hidden={!emailAccessEnabled}>
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.8fr)]">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-bold text-slate-700">Subject</label>
                      <Input value={emailSubject} onChange={(event) => setEmailSubject(event.target.value)} placeholder="What should followers see in their inbox?" className="mt-2 h-11 rounded-xl" maxLength={180} />
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-700">Message</label>
                      <div className="mt-2">
                        <RichTextEditor value={emailMessage} onChange={setEmailMessage} testId="market-email-message-editor" imageUploadEndpoint="/api/upload/message-image" videoUploadEndpoint="/api/upload/message-video" token={token} />
                      </div>
                      <p className="mt-2 text-xs text-slate-500">Use the image button to upload an image or GIF.</p>
                    </div>
                    {emailDraft?.updated_at && (
                      <p className="text-xs text-slate-500">Draft saved {formatEmailLogDate(emailDraft.updated_at)}.</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={handleSaveEmailDraft} disabled={savingDraft || (!emailSubject.trim() && !emailMessage.trim())} variant="outline" className="rounded-full px-6 font-bold">
                        {savingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Draft
                      </Button>
                      {emailDraft?.updated_at && (
                        <Button onClick={handleDeleteEmailDraft} disabled={savingDraft} variant="ghost" className="rounded-full px-6 font-bold text-red-600 hover:bg-red-50 hover:text-red-700">
                          <Trash2 className="mr-2 h-4 w-4" /> Delete Draft
                        </Button>
                      )}
                      <Button onClick={handleSendTestMessage} disabled={sendingTestEmail || !emailSubject.trim() || !emailMessage.trim()} variant="outline" className="rounded-full px-6 font-bold">
                        {sendingTestEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send Test to Myself
                      </Button>
                      <Button onClick={handleSendMessage} disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()} className="rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]">
                        {sendingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send Message
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Preview</p>
                    <div className="rounded-xl bg-slate-50 p-5">
                      <div className="mb-5 rounded-2xl bg-black px-5 py-6 text-center">
                        <div className="font-black leading-tight tracking-wide text-white" style={{ fontFamily: "'Outfit', sans-serif", fontSize: "28px" }}>
                          <div>614</div>
                          <div>SHOP</div>
                          <div>LOCAL</div>
                        </div>
                      </div>
                      <h3 className="mb-3 text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{emailSubject || "Your subject will appear here"}</h3>
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Message from {market.market_name || "your market"}</p>
                      {emailMessage ? (
                        <div className="prose prose-sm max-w-none text-slate-600 [&_a]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1" dangerouslySetInnerHTML={{ __html: emailMessage }} />
                      ) : (
                        <p className="text-sm leading-relaxed text-slate-600">Your message preview will appear here.</p>
                      )}
                      <div className="mt-5 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
                        {getLogoUrl(market.logo) ? (
                          <img src={getLogoUrl(market.logo)} alt={`${market.market_name || "Market"} logo`} className="h-14 w-14 rounded-2xl object-cover" />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#42D78A] text-2xl font-black text-[#1a1a1a]">
                            {(market.market_name || "M").slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Sent by</p>
                          <p className="truncate text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name || "Your market"}</p>
                        </div>
                      </div>
                      <p className="mt-5 border-t border-slate-200 pt-4 text-xs text-slate-400">This is an automated message from 614 Shop Local.</p>
                    </div>
                  </div>
                </div>
                </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="market-email-log-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-[#42D78A]" /> Email Log
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {emailLogs.length === 0 ? (
                    <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No emails sent yet.</p>
                  ) : emailLogs.map((log) => (
                    <button
                      key={log.id}
                      type="button"
                      onClick={() => setSelectedEmailLog(log)}
                      className="grid w-full grid-cols-1 gap-3 rounded-xl bg-slate-50 p-4 text-left transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
                      data-testid={`market-email-log-${log.id}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{log.subject || log.template_name || "Untitled email"}</p>
                        <p className="mt-1 text-sm text-slate-500">{formatEmailLogDate(log.created_at)} · Click to view</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-white text-slate-700 shadow-sm">{log.success_count || 0}/{log.recipient_count || 0} sent</Badge>
                        <Badge className={log.status === "sent" ? "bg-[#42D78A]/15 text-[#157347]" : log.status === "partial" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}>
                          {log.status || "unknown"}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="announcements" className="mt-0">
            <AnnouncementsPanel token={token} onUnreadCountChange={setAnnouncementUnreadCount} testId="market-announcements-panel" />
          </TabsContent>
        </Tabs>

        <EventFormModal open={showEventForm} onClose={() => { setShowEventForm(false); setEditingEvent(null); }} token={token} onSuccess={fetchMyEvents} editEvent={editingEvent} hostId={selectedMarketId} />
        <EmailLogDialog log={selectedEmailLog} open={!!selectedEmailLog} onOpenChange={(open) => !open && setSelectedEmailLog(null)} />
        <Dialog open={showAddVendorModal} onOpenChange={setShowAddVendorModal}>
          <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Add Vendor</DialogTitle>
              <DialogDescription>
                Search approved businesses and add them to {market.market_name || "this market"}.
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <Input
                value={vendorSearchTerm}
                onChange={(event) => setVendorSearchTerm(event.target.value)}
                placeholder="Search business names, categories, or locations..."
                className="h-12 rounded-full pl-12"
                data-testid="add-vendor-search"
              />
            </div>
            {loadingAvailableVendors ? (
              <div className="flex items-center justify-center rounded-xl bg-slate-50 p-8 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading businesses
              </div>
            ) : filteredAvailableVendors.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
                No businesses found.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {filteredAvailableVendors.map((business) => (
                  <VendorBusinessCard
                    key={vendorIdentifier(business)}
                    business={business}
                    adding={addingVendorId === vendorIdentifier(business)}
                    onView={() => setSelectedVendorBusiness(business)}
                    onAdd={() => handleAddVendor(business)}
                  />
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
        <BusinessDetailModal
          business={selectedVendorBusiness}
          open={!!selectedVendorBusiness}
          onClose={() => setSelectedVendorBusiness(null)}
          isSaved={false}
          onFollow={() => {}}
        />
      </main>
    </div>
  );
};

const VendorBusinessCard = ({ business, adding, onView, onAdd }) => {
  const logoUrl = getLogoUrl(business.logo);
  const identifier = vendorIdentifier(business);
  const status = business.vendor_status;
  const isAdded = status === "approved";
  const isPending = status === "pending";
  const disabled = adding || isAdded || isPending || !identifier;
  const statusLabel = isAdded ? "Added" : isPending ? "Pending" : "Add";

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onView}
          className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-slate-100"
          data-testid={`view-add-vendor-business-${identifier || "missing"}`}
        >
          {logoUrl ? (
            <img src={logoUrl} alt={business.business_name || "Business logo"} className="h-full w-full object-cover" />
          ) : (
            <span className="text-2xl font-black text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {(business.business_name || "B").slice(0, 1).toUpperCase()}
            </span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <button type="button" onClick={onView} className="block w-full text-left">
            <p className="truncate font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{business.business_name || "Unnamed Business"}</p>
            {business.business_type && <p className="mt-1 text-sm text-slate-500">{business.business_type}</p>}
            {business.business_locations?.length > 0 && (
              <p className="mt-1 truncate text-sm text-slate-500">{business.business_locations.join(", ")}</p>
            )}
          </button>
          {business.business_description && (
            <p className="mt-2 line-clamp-2 text-sm text-slate-600">{business.business_description}</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onView}
          className="rounded-full bg-white"
        >
          <Eye className="mr-1.5 h-4 w-4" /> View
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          className={isAdded || isPending ? "rounded-full bg-slate-100 text-slate-500 hover:bg-slate-100" : "rounded-full bg-[#42D78A] text-[#1a1a1a] hover:bg-[#3bc27c]"}
          data-testid={`add-vendor-${identifier || "missing"}`}
        >
          {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : isAdded ? <CheckCircle className="mr-1.5 h-4 w-4" /> : <Plus className="mr-1.5 h-4 w-4" />}
          {statusLabel}
        </Button>
      </div>
    </div>
  );
};

export default MarketDashboardPage;
